import React, { useState } from 'react';
import { X, XCircle, AlertCircle } from 'lucide-react';

/**
 * Modal pour annuler une facture avec raison
 */
const CancelInvoiceModal = ({ isOpen, onClose, invoice, onConfirm }) => {
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);
  const [notifyClient, setNotifyClient] = useState(true);

  if (!isOpen || !invoice) return null;

  // ⛔ Vérifier si la facture est payée
  const isFacturePayee = invoice.statut === 'payée';

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Bloquer si facture payée
    if (isFacturePayee) {
      setError('Impossible d\'annuler une facture payée. Cette action est bloquée pour des raisons comptables et légales.');
      return;
    }

    if (!reason.trim()) {
      setError('Veuillez indiquer la raison de l\'annulation');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('❌ Annulation de la facture:', invoice.id);

      // Appeler la fonction de confirmation
      await onConfirm(invoice, reason, notifyClient);

      console.log('✅ Facture annulée avec succès');
      onClose();

    } catch (err) {
      console.error('❌ Erreur annulation facture:', err);
      setError(err.message || 'Échec de l\'annulation');
    } finally {
      setLoading(false);
    }
  };

  const formatEuro = (montant) => {
    if (typeof montant === 'undefined' || montant === null) return '0,00 €';
    return montant.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('fr-FR');
  };

  // Raisons prédéfinies
  const predefinedReasons = [
    'Erreur de facturation',
    'Demande du client',
    'Rendez-vous annulés',
    'Modification des heures',
    'Doublon',
    'Autre (à préciser)'
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* En-tête */}
        <div className="bg-orange-50 dark:bg-orange-900/20 border-b border-orange-200 dark:border-orange-800 p-6 flex justify-between items-center rounded-t-lg">
          <div className="flex items-center">
            <div className="flex items-center justify-center w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-full mr-4">
              <XCircle className="w-6 h-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-orange-900 dark:text-orange-100">
                Annuler la facture
              </h2>
              <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
                La facture sera conservée avec le statut "annulée"
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-orange-400 hover:text-orange-600 dark:hover:text-orange-300"
            disabled={loading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Contenu */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Détails de la facture */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
              Facture à annuler
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Numéro :</span>
                <span className="font-mono text-gray-900 dark:text-white">{invoice.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Client :</span>
                <span className="text-gray-900 dark:text-white">{invoice.client?.nom || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Période :</span>
                <span className="text-gray-900 dark:text-white">{invoice.periode?.libelle || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Date :</span>
                <span className="text-gray-900 dark:text-white">{formatDate(invoice.dateGeneration)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Montant :</span>
                <span className="font-bold text-gray-900 dark:text-white">
                  {formatEuro(invoice.calcul?.montantTotal || 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Statut actuel :</span>
                <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded text-xs font-medium">
                  {invoice.statut}
                </span>
              </div>
            </div>
          </div>

          {/* ⛔ Blocage si facture payée */}
          {isFacturePayee && (
            <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 rounded">
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-red-900 dark:text-red-100 mb-1">
                    ⛔ Annulation bloquée
                  </h3>
                  <p className="text-sm text-red-800 dark:text-red-200">
                    Cette facture a été <strong>payée</strong> et ne peut pas être annulée pour des raisons
                    comptables et légales. Si vous devez effectuer une correction :
                  </p>
                  <ul className="mt-2 space-y-1 text-sm text-red-800 dark:text-red-200 list-disc list-inside">
                    <li>Émettez un avoir ou une note de crédit</li>
                    <li>Contactez le support pour assistance</li>
                    <li>Gérez le remboursement manuellement auprès du client</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Raisons prédéfinies */}
          {!isFacturePayee && (
            <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Raison de l'annulation *
            </label>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {predefinedReasons.map((predefinedReason) => (
                <button
                  key={predefinedReason}
                  type="button"
                  onClick={() => setReason(predefinedReason)}
                  className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                    reason === predefinedReason
                      ? 'bg-orange-100 dark:bg-orange-900/30 border-orange-500 text-orange-900 dark:text-orange-100'
                      : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
                  disabled={loading}
                >
                  {predefinedReason}
                </button>
              ))}
            </div>

            {/* Champ de saisie personnalisé */}
            <textarea
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setError(null);
              }}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-gray-700 dark:text-white"
              rows="4"
              placeholder="Précisez la raison de l'annulation..."
              required
              disabled={loading}
            />
          </div>
          )}

          {/* Messages d'erreur */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-sm text-red-800 dark:text-red-200">
                {error}
              </p>
            </div>
          )}

          {/* Option notification client */}
          {!isFacturePayee && (
            <div className="flex items-start">
            <input
              type="checkbox"
              id="notifyClient"
              checked={notifyClient}
              onChange={(e) => setNotifyClient(e.target.checked)}
              className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500 mt-1"
              disabled={loading}
            />
            <label htmlFor="notifyClient" className="ml-3">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Notifier le client par email
              </span>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Le client recevra un email l'informant de l'annulation de la facture avec la raison indiquée.
              </p>
            </label>
          </div>
          )}

          {/* Information */}
          {!isFacturePayee && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3" />
                <div className="text-sm text-blue-900 dark:text-blue-100">
                  <p className="font-medium mb-1">Que se passe-t-il après l'annulation ?</p>
                  <ul className="list-disc list-inside space-y-1 text-blue-800 dark:text-blue-200">
                    <li>La facture passe au statut "annulée"</li>
                    <li>Elle reste visible dans l'historique</li>
                    <li>La raison d'annulation est enregistrée</li>
                    <li>Le produit Stripe reste actif (non supprimé)</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Boutons d'action */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              disabled={loading}
            >
              {isFacturePayee ? 'Fermer' : 'Annuler'}
            </button>
            {!isFacturePayee && (
              <button
                type="submit"
                className="flex items-center px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading || !reason.trim()}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Annulation...
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4 mr-2" />
                    Annuler la facture
                  </>
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default CancelInvoiceModal;
